/**
 * Traditional Chinese (繁體中文) — core/shell strings. Product names stay
 * untranslated (brand). Completeness is enforced by the aggregator (zh-TW.ts),
 * so this module is a plain object, not typed as Messages.
 */
const zhTW = {
  common: {
    initialising: '初始化中…',
    loading: '載入中…',
    beta: '測試版',
    authUnavailableTitle: '無法使用驗證服務',
    authUnavailableBody: '無法初始化安全登入。請檢查連線後再試一次。',
    retry: '重試',
  },
  nav: {
    home: '首頁',
    diagnosis: 'Diagnosis',
    meshgen: 'MeshGen',
    simops: 'SimOps',
    evaltool: 'EvalTool',
    assistant: 'AI 助理',
    feedback: '意見回饋',
    whatsNew: '最新消息',
    account: '帳戶',
    logout: '登出',
    logoutUnavailable: '沒有可登出的帳戶',
  },
  evaltoolLibrary: {
    loadError: '無法載入研究',
    emptyTitle: '目前沒有可評估的研究',
    emptyHint: '完成的 SimOps 研究會顯示在這裡，供你檢視。',
  },
  whatsNew: {
    title: '最新消息',
    readMore: '閱讀更多',
    more: '還有 {{count}} 項',
  },
  account: {
    title: '帳戶',
    profile: '個人資料',
    firstName: '名字',
    lastName: '姓氏',
    email: '電子郵件',
    save: '儲存',
    saving: '儲存中…',
    saved: '已儲存',
    theme: '主題',
    themeLight: '淺色',
    themeDark: '深色',
    themeSystem: '系統',
    themeAcademic: '學術',
    language: '語言',
    signOut: '登出',
    signOutDescription: '結束此裝置上的工作階段。',
    userId: '使用者 ID',
    usage: {
      title: '方案與用量',
      loadError: '無法載入方案。',
      usageUnavailable: '無法載入用量數字。以下顯示你的方案。',
      notReadable: '無法載入用量。',
      resetsOn: '{{date}} 重新計算。',
      trialEndsOn: '試用期於 {{date}} 結束。',
      trialNeedsCard: '請在帳務入口新增信用卡，以在試用期後繼續使用。',
      noPeriod: '尚未開始計算用量 —— 這個組織還沒有計費週期。',
      unlimited: '無限',
      notIncluded: '未包含',
      notCounted: '尚未開始計算。',
      usedThisPeriod: '本週期已使用 {{used}}。',
      usedOfAllowance: '{{used}} / {{allowance}}',
      remaining: '剩餘 {{remaining}}',
      past: '已超出方案包含的用量 {{excess}}',
      limitReached: '已達上限',
      extra: '超出',
      cost: '費用',
      rate: '超出部分以 {{rate}} 計費。',
      exhaustedTitle: '方案包含的用量已用完',
      exhaustedBodyOwner: '新增付款方式即可繼續，或等到週期重新計算。',
      exhaustedBodyMember: '請組織擁有者新增付款方式，或等到週期重新計算。',
      manage: '管理訂閱',
      manageOpening: '開啟中…',
      manageFailed: '無法開啟帳單入口，請再試一次。',
      status: {
        active: '使用中',
        trialing: '試用中',
        pastDue: '付款失敗',
        unpaid: '已暫停存取',
        canceled: '已取消',
        paused: '已暫停',
      },
      seatsPurchasedProduct_one: '{{product}}：{{count}} 個付費席次',
      seatsPurchasedProduct_other: '{{product}}：{{count}} 個付費席次',
      seatsPurchasedProductScheduled_one:
        '{{product}}：{{count}} 個付費席次，自 {{date}} 起 {{scheduled}} 個',
      seatsPurchasedProductScheduled_other:
        '{{product}}：{{count}} 個付費席次，自 {{date}} 起 {{scheduled}} 個',
      buy: '購買席次',
      changePlan: '變更方案',
      pendingTitle: '已收到付款',
      pendingBody: '正在更新你的方案，通常只需要幾秒鐘。',
      pendingSlowBody:
        '方案更新的時間比平常久。你的付款已完成，請稍後再回來看看。',
      dunningRetryingTitle: '請更新你的卡片',
      dunningRetryingBody: '你上次的付款失敗了。請更新卡片以維持存取權限。',
      dunningPausedTitle: '你的訂閱尚未付款',
      dunningPausedBody:
        '在未付款的帳單付清之前，存取權限將維持暫停。請在帳單入口更新卡片並完成付款。',
      updateCard: '更新卡片',
      cancelledTitle: '已取消結帳',
      cancelledBody: '沒有扣款。',
      dismiss: '關閉',
      ownerOnly: '只有組織擁有者可以變更訂閱。',
      pendingChangeTitle: '預約的變更',
      pendingChange:
        '{{product}}：{{current}} 到 {{date}}，之後改為 {{next}}。',
      pendingChangeRemoved: '{{product}}：{{current}} 到 {{date}}，之後移除。',
      pendingChangeTo: '{{product}}：自 {{date}} 起改為 {{next}}。',
      pendingChangeDropped: '{{product}}：自 {{date}} 起移除。',
      pendingChangeSeats_one: '{{product}}：自 {{date}} 起 {{count}} 個席次。',
      pendingChangeSeats_other:
        '{{product}}：自 {{date}} 起 {{count}} 個席次。',
      cancelScheduled: '取消預約的變更',
      cancelScheduledPending: '取消中…',
      cancelScheduledFailed: '無法取消預約的變更，請再試一次。',
    },
    workspaces: {
      title: '工作區',
      description:
        '您屬於多個工作區。切換後應用程式會重新載入，並以您選擇的工作區登入。',
      current: '目前',
      switch: '切換',
      switching: '切換中…',
      switchError: '無法開啟該工作區。請再試一次，或登出後再登入該工作區。',
    },
    organization: {
      title: '組織',
      loading: '正在載入組織…',
      loadError: '無法載入組織資料。',
      unavailableTitle: '無法使用組織',
      unavailableBody: '你的帳戶尚未連結至有效組織。',
      teamLabel: '工作區',
      readOnlyTitle: '工作區成員資格',
      readOnlyBody:
        '只有組織擁有者能管理成員與電子郵件存取網域。你的存取權不受影響。',
      organizationId: '組織 ID',
      yourRole: '你的角色',
      ownerControls: '擁有者控制',
      statusActive: '啟用',
      statusSuspended: '已停權',
      billingBoundaryTitle: '成員資格不等於計費',
      billingBoundaryBody:
        '此處的成員變更不會啟用方案或同步計費席次數量。商務開通仍由其他流程處理。',
      renameTitle: '工作區名稱',
      renameDescription: '會顯示在成員的工作區選擇器與邀請中。',
      renameLabel: '名稱',
      saveName: '儲存名稱',
      renamed: '工作區名稱已儲存。',
      domainsTitle: '電子郵件存取網域',
      domainsDescription: '將 Stytch 電子郵件即時存取限制於這些網域。',
      domainsOwnershipBoundary:
        '此允許清單不代表 DNS 所有權，也不會自行建立 Khorium 成員資格。Stytch 仍要求已驗證的電子郵件地址。',
      domainsLabel: '允許的網域',
      domainsPlaceholder: 'example.com\nsubsidiary.example',
      domainsEmpty: '未允許任何網域。電子郵件即時存取已關閉。',
      saveDomains: '儲存網域',
      domainsSaved: '已儲存電子郵件存取網域。',
      membersTitle: '成員',
      memberCount: '{{count}} 位成員',
      seatsProductAssigned:
        '{{product}} 已指派 {{assigned}} / {{purchased}} 席',
      seatsProductAssignedScheduled:
        '{{product}} 已指派 {{assigned}} / {{purchased}} 席（續約時 {{scheduled}} 席）',
      seatsProductUnlimited: '{{product}} 已指派 {{count}} 席 · 無上限',
      seatsUnavailable: '席位上限暫時無法取得',
      seatsRetry: '重試',
      seatUnassigned: '未指派席位',
      assignProductSeat: '指派 {{product}} 席位',
      unassignProductSeat: '收回 {{product}} 席位',
      inviteTitle: '邀請成員',
      inviteDescription:
        '接受邀請後才會取得團隊成員資格。應用程式權限與資源分享權限分開設定。',
      emailLabel: '電子郵件地址',
      emailPlaceholder: 'name@company.com',
      inviteRole: '組織角色',
      invite: '傳送邀請',
      inviting: '傳送中…',
      inviteSent: '已將邀請傳送至 {{email}}。',
      noMembers: '找不到成員。',
      you: '你',
      roleOwner: '擁有者',
      roleAdmin: '管理員',
      roleMember: '成員',
      statusInvited: '已邀請',
      changeRole: '變更角色',
      suspend: '停權',
      reactivate: '重新啟用',
      remove: '移除',
      transferOwnership: '移轉擁有權',
      roleConfirmTitle: '要將 {{email}} 變更為{{role}}嗎？',
      roleConfirmBody: '只有組織權限會變更。現有資源分享權限仍分開保留。',
      suspendConfirmTitle: '要停權 {{email}} 嗎？',
      suspendConfirmBody:
        '存取權會立即停止，所擁有的專案會移至目前擁有者。重新啟用只會恢復成員資格，不會恢復原專案擁有權。',
      reactivateConfirmTitle: '要重新啟用 {{email}} 嗎？',
      reactivateConfirmBody:
        '會依目前角色恢復團隊存取。停權期間移轉的專案仍歸目前擁有者。',
      removeConfirmTitle: '要移除 {{email}} 嗎？',
      removeConfirmBody:
        '成員資格會被刪除，所擁有的專案會移至目前擁有者。留言與現有資源分享仍會保留。',
      transferConfirmTitle: '要將擁有權移轉給 {{email}} 嗎？',
      transferConfirmBody:
        '你將成為管理員，所選成員會成為唯一擁有者。計費分開管理，不會移轉或啟用。',
      confirm: '確認',
      cancel: '取消',
      updating: '更新中…',
      roleUpdated: '角色已更新。',
      memberSuspended: '成員已停權。',
      memberReactivated: '成員已重新啟用。',
      memberRemoved: '成員已移除。',
      ownershipTransferred: '擁有權已移轉。',
      actionFailed: '無法完成操作：{{reason}}',
      retry: '重試',
    },
  },
  checkout: {
    title: '購買席次',
    subtitle: '選擇組織需要的項目，以及每個項目的席次數量。',
    productsLabel: '包含哪些項目',
    productMeshgenBody: '從幾何產生網格。',
    productSimopsBody: '執行模擬與研究。',
    productDiagnosisBody: '在網格化之前檢查幾何問題。',
    tierNone: '無',
    tierNoneBody: '不包含。',
    currentPlan: '目前方案',
    tierBody: '每席位每月 {{included}} · 每席位 ${{price}}',
    overageNote:
      '超出包含的分鐘數後，工作會以每小時 ${{rate}} 繼續執行，按月計費。',
    seatsFor: '{{product}} 席次',
    seatsHelp: '每個在該產品中執行工作的人一個席次。購買後可以再指派給成員。',
    lineItem_one: '{{plan}} · {{count}} 個席位 · 每月 ${{amount}}',
    lineItem_other: '{{plan}} · {{count}} 個席位 · 每月 ${{amount}}',
    total: '每月 ${{amount}}',
    trialTotal: '免費 {{days}} 天，之後每月 ${{amount}} —— 需要信用卡。',
    priceNote: '會在下一頁付款前確認。',
    submit: '前往付款',
    submitting: '正在開啟結帳…',
    failed: '無法開啟結帳。沒有扣款。',
    ownerOnly: '只有這個組織的擁有者可以購買席次。',
    unavailable: '目前尚未開放購買席次。',
    backToAccount: '回到帳戶',
    changeTitle: '變更方案',
    changeOwnerOnly: '只有這個組織的擁有者可以變更方案。',
    changeUnavailable: '目前尚未開放變更方案。',
    changePaused:
      '你上次的付款失敗，存取權限已暫停。變更方案前，請從帳戶頁面前往帳單入口付清未付款的帳單。',
    changeSubtitle:
      '把每個產品在「無」、Basic 與 Plus 之間移動，並設定每個產品的席次數量。',
    changeNote:
      '升級或增加席次會在今天收費並立即生效。降級或減少席次會在下次續約時生效，不會收費也不會退款。',
    changeSubmit: '變更方案',
    changeSubmitting: '正在變更方案…',
    submitPayToday: '今天支付 {{amount}}',
    submitSchedule: '預約變更',
    submitPayAndSchedule: '今天支付 {{amount}} 並預約其餘變更',
    changeFailed: '無法變更方案。沒有扣款。',
    cardDeclined:
      '你的卡片被拒絕，沒有任何變更。請到帳單入口更新卡片後再試一次。',
    cancelScheduledFirst: '已經有一個預約的方案變更。請先到帳戶頁取消它。',
    changeDone: '方案已變更。這裡可能需要一點時間才會更新。',
    changeScheduled:
      '方案已變更。降級的部分會在 {{date}} 生效。這裡可能需要一點時間才會更新。',
    changePickAPlan: '為每個產品選擇方案，就會顯示費用與生效時間。',
    changeNothingYet: '選擇不同的方案，就會顯示費用與生效時間。',
    changeKeepOne:
      '請至少保留一個產品。若要結束訂閱，請在帳戶頁使用「管理訂閱」。',
    currentPlanUnknown: '這個產品目前的方案不在這三個之中，請選擇你要的方案。',
    seatsUnbounded: '這個產品沒有席次上限。',
    changeSeatsInvalid: '為每個產品設定 1 到 100 個席次，就會顯示費用。',
    seatsBelowAssignedLine:
      '{{product}}：{{assigned}} 位成員持有席位，但只要求 {{requested}} 個席次。',
    seatsBelowAssigned:
      '產品的席次不能少於持有席位的成員數。請先收回成員的席位。',
    manageSeats: '到帳戶頁管理席位',
    chargedToday: '今天收費：{{amount}}',
    scheduledFrom: '自 {{date}} 起：{{plans}}',
    scheduledFromUndated: '自下次續約起：{{plans}}',
    nothingToday: '今天不會收費。',
    previewPending: '正在計算費用…',
    previewFailed: '目前無法計算費用。',
  },
  home: {
    greetingMorning: '早安，',
    greetingAfternoon: '午安，',
    greetingEvening: '晚安，',
    fallbackName: '您好',
    importRepair: '修復',
    importMesh: '網格',
    newStudy: '新增研究',
    noProjects: '尚無專案。上傳檔案即可開始。',
    ask: {
      heading: '您想處理什麼？',
      placeholder: '詢問網格、研究或失敗原因…',
      submit: '詢問',
      footnote: '您的問題會以全螢幕開啟助理，成為新的對話。',
      dropHint: '將 CAD 零件拖放到這裡或加以附加，即可詢問助理。',
      dropActive: '放開即可附加到您的問題',
      leadSuggestion: '{{name}} 為什麼失敗？是否應該重試？',
      suggestionFailure: '我最近的網格為什麼失敗？',
      suggestionSummarise: '摘要目前執行中的研究進度',
      suggestionStudy: '如何從網格建立研究？',
    },
    work: {
      title: '您的工作',
      onYou: '{{count}} 項待您處理',
      review: '檢視',
      reviewRetry: '檢視重試',
      empty: '沒有執行中的工作，也沒有待您處理的項目。',
      error: '無法載入您的工作。',
      partial: '部分工作無法載入。',
      allActivity: '所有活動',
    },
    recents: {
      title: '您上次的位置',
      allMeshes: '所有網格',
      allStudies: '所有研究',
      filterAll: '全部',
      filterMeshing: '網格',
      filterStudies: '研究',
      count: '最近 {{count}} 項',
      countOf: '{{total}} 項中的 {{shown}} 項',
      emptyFilter: '這個分類沒有最近的工作。',
      error: '無法載入最近的工作。',
      partial: '部分最近的工作無法載入。',
    },
  },
};

export default zhTW;
